import { NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NgcCookieConsentService } from 'ngx-cookieconsent';
import { finalize, tap } from 'rxjs/operators';
import { AuthService } from './platform/services/auth.service';
import { CookieService } from './platform/services/cookie.service';
import {
  PendingDocumentReviewCheckResponse,
  PostLoginChecksService,
  UserTrainingScopeResponse,
  UserTrainingByRoleVideoResponse,
} from './platform/services/post-login-checks.service';
import { SessionExpiryService } from './platform/services/session-expiry.service';
import { VersionUpdateService } from './platform/services/version-update.service';
import { DialogComponent } from './platform/shared/dialog/dialog.component';
import { IdleComponent } from './platform/shared/idle/idle.component';
import { LoaderComponent } from './platform/shared/loader/loader.component';
import { StringEmpty } from './platform/utils/global-vars';
import { ApplicationUserService } from './platform/utils/application-user.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [NgClass, IdleComponent, RouterOutlet, LoaderComponent, DialogComponent],
})
export class AppComponent implements OnInit {
  @ViewChild('pendingReviewDialog') public pendingReviewDialog: DialogComponent;

  public isBackgroundImage = false;
  public pendingReviewDialogMessage = StringEmpty;
  public pendingReviewDialogAdditionalInfo = StringEmpty;
  public pendingReviewUsername = StringEmpty;

  private pendingTrainingQueue: UserTrainingByRoleVideoResponse[] = [];
  private activeTrainingKey = StringEmpty;
  private lastTrainingRedirectUrl = StringEmpty;
  private isProcessingPostLoginNavigation = false;
  private hasPendingDocumentReview = false;
  private hasShownPendingDocumentReview = false;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly translate: TranslateService,
    private readonly cookieService: CookieService,
    private readonly router: Router,
    public readonly authService: AuthService,
    private ccService: NgcCookieConsentService,
    private readonly versionUpdateService: VersionUpdateService,
    private readonly postLoginChecksService: PostLoginChecksService,
    private readonly applicationUserService: ApplicationUserService, // NO DELETE! not used directly in this component, but we want to initialize it at the app level to have the user data available as soon as possible
    private readonly sessionExpiryService: SessionExpiryService, // NO DELETE! instantiated app-wide so it watches the Keycloak session lifetime from bootstrap
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (this.isTrainingRoute(event.urlAfterRedirects)) {
          this.lastTrainingRedirectUrl = StringEmpty;
        }

        if (!this.authService.userIsLogged() || event.urlAfterRedirects.startsWith('/login')) {
          this.resetPostLoginState();
        }

        if (this.isErrorRoute(event.urlAfterRedirects)) {
          this.activeTrainingKey = StringEmpty;
          return;
        }

        if (this.isProcessingPostLoginNavigation) {
          return;
        }

        this.isProcessingPostLoginNavigation = true;

        const shouldRefreshTrainingStatus = this.shouldRefreshTrainingStatus(event.urlAfterRedirects);

        this.postLoginChecksService
          .tryRunAfterNavigation(event.urlAfterRedirects, shouldRefreshTrainingStatus)
          .pipe(
            finalize(() => {
              this.isProcessingPostLoginNavigation = false;
            }),
          )
          .subscribe({
            next: (response) => {
              if (shouldRefreshTrainingStatus) {
                this.pendingTrainingQueue = response ? this.extractPendingTrainingQueue(response.trainingByRole) : [];
                this.activeTrainingKey = StringEmpty;

                if (response?.hasDocumentsToReview) {
                  this.hasPendingDocumentReview = true;
                  this.pendingReviewUsername = response.username;
                } else {
                  this.hasPendingDocumentReview = false;
                }
              } else if (response) {
                if (this.pendingTrainingQueue.length === 0) {
                  this.pendingTrainingQueue = this.extractPendingTrainingQueue(response.trainingByRole);
                }

                if (!this.hasShownPendingDocumentReview && response.hasDocumentsToReview) {
                  this.hasPendingDocumentReview = true;
                  this.pendingReviewUsername = response.username;
                }
              }

              if (this.redirectToNextTrainingIfNeeded(event.urlAfterRedirects)) {
                return;
              }

              if (this.hasPendingDocumentReview && !this.hasShownPendingDocumentReview) {
                this.showPendingReviewDialog({
                  username: this.pendingReviewUsername,
                  hasDocumentsToReview: true,
                  trainingByRole: [],
                });
                this.hasShownPendingDocumentReview = true;
              }
            },
            error: (error) => {
              console.error('Post-login check failed', error);
            },
          });

        if (event.urlAfterRedirects == '/login') {
          this.isBackgroundImage = true;
        } else {
          this.isBackgroundImage = false;
        }
        $(() => {
          $('body').tooltip({ selector: '[data-toggle=tooltip]' });
        });
      }

      if (event instanceof NavigationStart) {
        $('body > .tooltip').remove();
      }
    });
  }

  ngOnInit() {
    this.versionUpdateService.start();
    this.translate.addLangs(['en', 'it']);
    this.translate.setDefaultLang('en');

    const browserLang = this.translate.getBrowserLang();
    this.translate.use(browserLang.match(/en|it/) ? browserLang : 'en').pipe(
      tap((_) => {
        this.translate //
          .get(['cookie.header', 'cookie.message', 'cookie.dismiss', 'cookie.allow', 'cookie.deny', 'cookie.link', 'cookie.policy'])
          .subscribe((data) => {
            this.ccService.getConfig().content = this.ccService.getConfig().content || {};
            // Override default messages with the translated ones
            this.ccService.getConfig().content.header = data['cookie.header'];
            this.ccService.getConfig().content.message = data['cookie.message'];
            this.ccService.getConfig().content.dismiss = data['cookie.dismiss'];
            this.ccService.getConfig().content.allow = data['cookie.allow'];
            this.ccService.getConfig().content.deny = data['cookie.deny'];
            this.ccService.getConfig().content.link = data['cookie.link'];
            this.ccService.getConfig().content.policy = data['cookie.policy'];

            this.ccService.destroy(); // remove previous cookie bar (with default messages)
            this.ccService.init(this.ccService.getConfig()); // update config with translated messages
          });
      }),
    );
  }

  public onPendingReviewDialogAction(confirm: boolean): void {
    if (!confirm || !this.pendingReviewUsername) {
      return;
    }

    void this.router.navigate([`/profile/${this.pendingReviewUsername}/user-documents`]);
  }

  private showPendingReviewDialog(response: PendingDocumentReviewCheckResponse): void {
    this.pendingReviewUsername = response.username;
    this.pendingReviewDialogMessage = this.translate.instant('platform.user-documents.review-required.modal.title');
    this.pendingReviewDialogAdditionalInfo = this.translate.instant('platform.user-documents.review-required.modal.info');

    setTimeout(() => {
      this.cdr.detectChanges();

      if (!this.pendingReviewDialog) {
        console.error('Pending review dialog ViewChild is not available.');
        return;
      }

      this.pendingReviewDialog.showDialog();
    }, 0);
  }

  private extractPendingTrainingQueue(trainingByRole: UserTrainingByRoleVideoResponse[] | null | undefined): UserTrainingByRoleVideoResponse[] {
    return (trainingByRole || []).filter((role) => this.getFirstTrainingScope(role) !== null);
  }

  private redirectToNextTrainingIfNeeded(currentUrl: string): boolean {
    if (this.isTrainingRoute(currentUrl)) {
      return false;
    }

    if (this.pendingTrainingQueue.length === 0) {
      this.activeTrainingKey = StringEmpty;
      return false;
    }

    const nextTraining = this.pendingTrainingQueue[0];
    const scope = this.getFirstTrainingScope(nextTraining);
    if (!scope) {
      this.pendingTrainingQueue.shift();
      return false;
    }

    const targetUrl = `/organizations/${scope.organizationId}/study/${scope.studyId}/training`;
    if (this.lastTrainingRedirectUrl === targetUrl) {
      return false;
    }

    this.activeTrainingKey = this.toTrainingKey(scope);
    this.lastTrainingRedirectUrl = targetUrl;
    void this.router.navigate([targetUrl], {
      state: {
        postLoginTraining: {
          role: nextTraining.role,
          scopes: nextTraining.scopes || [],
        },
      },
    });
    return true;
  }

  private isTrainingRoute(url: string): boolean {
    return /\/organizations\/[^/]+\/study\/[^/]+\/training(?:$|\?)/.test(url);
  }

  private shouldRefreshTrainingStatus(currentUrl: string): boolean {
    return !!this.activeTrainingKey && !this.isTrainingRoute(currentUrl) && !this.isErrorRoute(currentUrl);
  }

  private isErrorRoute(url: string): boolean {
    return /^\/error(?:$|\/|\?)/.test(url);
  }

  private getFirstTrainingScope(role: UserTrainingByRoleVideoResponse | null | undefined): UserTrainingScopeResponse | null {
    if (!Array.isArray(role?.scopes) || role.scopes.length === 0) {
      return null;
    }

    const firstScope = role.scopes[0];
    if (!firstScope?.organizationId || !firstScope?.studyId) {
      return null;
    }

    return firstScope;
  }

  private toTrainingKey(scope: UserTrainingScopeResponse): string {
    return `${scope.organizationId}_${scope.studyId}`;
  }

  private resetPostLoginState(): void {
    this.pendingTrainingQueue = [];
    this.activeTrainingKey = StringEmpty;
    this.lastTrainingRedirectUrl = StringEmpty;
    this.isProcessingPostLoginNavigation = false;
    this.hasPendingDocumentReview = false;
    this.hasShownPendingDocumentReview = false;
    this.pendingReviewUsername = StringEmpty;
  }
}
